import React, { Suspense, lazy, useContext } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import 'react-toastify/dist/ReactToastify.css';
import "./styles.css";
import "./crm-design.css";

import AutoAppyLogin from '../features/Auth/AutoAppyLogin';
import DeveloperOAuthCallback from 'src/features/Auth/DeveloperOAuthCallback';
import { AuthContext } from '../context/AuthContext';
import DetailView from '../components/Dashboard/DetailView';
import StageSidebar from 'src/stage/StageSidebar';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import { WorkspaceProvider } from 'src/features/MiniCRM/Workspace/context/WorkspaceContext';

import ProtectedRoute from './ProtectedRoute';
import AdminRoute from './AdminRoute';
import WorkspaceRequiredRoute from './WorkspaceRequiredRoute';
import Leads from 'src/features/MiniCRM/Leads';
import WorkflowBuilder from 'src/features/MiniCRM/WorkflowBuilder';
import WorkflowEntry from 'src/features/MiniCRM/WorkflowBuilder/WorkflowEntry';
import WorkspaceEntryPage from 'src/features/MiniCRM/Workspace/pages/WorkspaceEntryPage';
import TeamPage from 'src/features/MiniCRM/Workspace/pages/TeamPage';
import TeamMemberPerformance from 'src/features/MiniCRM/Workspace/pages/TeamMemberPerformance';
import LeadFieldsPage from 'src/features/MiniCRM/LeadFields/LeadFieldsPage';
import DocumentsPage from 'src/features/MiniCRM/Documents/DocumentsPage';
import SettingsPage from 'src/features/Stage/SettingsPage';
import FollowUpsPage from 'src/features/Stage/FollowUpsPage';
import TargetsPage from 'src/features/Stage/targets/TargetsPage';
import { StageConnecting } from 'src/stage/StageStatus';

// Charts load only when Insights opens.
const InsightsPage = lazy(() => import('src/features/Stage/InsightsPage'));

Modal.setAppElement('#root');

// Public pages render without the product shell.
const PUBLIC_PATTERNS = [
  /^\/$/, /^\/login$/, /^\/signup$/, /^\/landing$/, /^\/about-us$/, /^\/contact-us$/,
  /^\/privacy-policy$/, /^\/terms-conditions$/, /^\/verify-email\/[^/]+$/, /^\/auth\//,
];

function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const { auth } = useContext(AuthContext);
  const showShell = auth && !PUBLIC_PATTERNS.some((pattern) => pattern.test(location.pathname));

  return (
    <WorkspaceProvider>
      <div className={`app-container full-screen ${showShell ? 'with-sidebar crm-stage-shell' : ''}`}>
        {showShell && <StageSidebar />}

        <div className="main-content">
          <Routes>
            {/* Public */}
            <Route path="/" element={<AutoAppyLogin />} />
            <Route path="/login" element={<AutoAppyLogin />} />
            <Route path="/auth/error" element={<AutoAppyLogin />} />
            <Route path="/auth/autoappy/developer/callback" element={<DeveloperOAuthCallback />} />
            <Route path="/dashboard" element={<Navigate to={`/crm${location.search}${location.hash}`} replace />} />

            {/* Daily work */}
            <Route path="/workspace" element={<ProtectedRoute element={<WorkspaceEntryPage />} />} />
            <Route path="/crm" element={<WorkspaceRequiredRoute element={<Leads />} />} />
            <Route path="/details/:id" element={<WorkspaceRequiredRoute element={<DetailView />} />} />
            <Route path="/follow-ups" element={<WorkspaceRequiredRoute element={<FollowUpsPage />} />} />
            <Route path="/targets" element={<WorkspaceRequiredRoute element={<TargetsPage />} />} />
            <Route path="/insights" element={<WorkspaceRequiredRoute element={<Suspense fallback={<StageConnecting />}><InsightsPage /></Suspense>} />} />
            <Route path="/documents" element={<WorkspaceRequiredRoute element={<DocumentsPage />} />} />
            <Route path="/team" element={<Navigate to="/teams" replace />} />
            <Route path="/teams" element={<WorkspaceRequiredRoute element={<TeamPage />} />} />
            <Route path="/teams/:userId" element={<WorkspaceRequiredRoute element={<TeamMemberPerformance />} />} />

            {/* Setup */}
            <Route path="/admin/workflow" element={<WorkspaceRequiredRoute element={<AdminRoute permission="pipeline.manage" element={<WorkflowEntry />} />} />} />
            <Route path="/admin/pipelines" element={<WorkspaceRequiredRoute element={<AdminRoute permission="pipeline.manage" element={<WorkflowEntry />} />} />} />
            <Route path="/admin/pipeline/:pipeline" element={<WorkspaceRequiredRoute element={<AdminRoute permission="pipeline.manage" element={<WorkflowBuilder />} />} />} />
            <Route path="/admin/lead-fields" element={<WorkspaceRequiredRoute element={<AdminRoute permission="lead-fields.manage" element={<LeadFieldsPage />} />} />} />

            {/* Settings (API keys moved here; the old URL still works) */}
            <Route path="/settings" element={<ProtectedRoute element={<SettingsPage />} />} />
            <Route path="/settings/:section" element={<ProtectedRoute element={<SettingsPage />} />} />
            <Route path="/api-keys" element={<Navigate to="/settings/api-keys" replace />} />
            <Route path="/profile" element={<ExternalRedirect to={autoAppyUrl('/profile')} />} />
          </Routes>
        </div>
      </div>
    </WorkspaceProvider>
  );
}

export default AppRoutes;
